import { useState } from 'react';
import { usePWAInstall } from '../hooks/usePWAInstall';

/**
 * PWA Install Prompt Component
 *
 * Displays a banner prompting users to install the app to their home screen.
 * Supports iOS Safari (with manual instructions) and Chrome/Edge (with native prompt).
 *
 * Features:
 * - Automatic detection of install capability
 * - Platform-specific install instructions
 * - Dismissible banner with local storage persistence
 * - Native install prompt for supported browsers
 */
export function PWAInstallPrompt() {
  const { isInstallable, isInstalled, promptInstall, dismissPrompt } =
    usePWAInstall();
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem('pwa-install-dismissed') === 'true';
  });

  // Don't show if already installed or dismissed
  if (isInstalled || isDismissed) {
    return null;
  }

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
  const isIOSSafari = isIOS && isSafari;

  const handleInstall = async () => {
    if (isIOSSafari) {
      // Show iOS-specific instructions
      setShowIOSInstructions(true);
    } else {
      // Use native install prompt for other browsers
      const accepted = await promptInstall();
      if (accepted) {
        console.log('PWA installation accepted');
      }
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('pwa-install-dismissed', 'true');
    dismissPrompt();
  };

  // Show install banner for installable apps or iOS Safari
  if (!isInstallable && !isIOSSafari) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-blue-600 text-white shadow-lg md:bottom-4 md:left-4 md:right-auto md:max-w-md md:rounded-lg">
      <div className="p-4">
        {!showIOSInstructions ? (
          <>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">
                  Install Permit Extractor
                </h3>
                <p className="text-sm text-blue-100">
                  Install this app on your device for quick access and offline
                  functionality
                </p>
              </div>
              <button
                onClick={handleDismiss}
                className="text-blue-200 hover:text-white p-1"
                aria-label="Dismiss install prompt"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={handleInstall}
                className="flex-1 bg-white text-blue-600 px-4 py-2 rounded font-semibold hover:bg-blue-50 transition-colors"
              >
                Install
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 rounded font-semibold text-blue-100 hover:text-white transition-colors"
              >
                Not Now
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">
                  Install on iOS
                </h3>
                <ol className="text-sm text-blue-100 space-y-2 list-decimal list-inside">
                  <li>
                    Tap the Share button{' '}
                    <span className="inline-block">
                      <svg
                        className="w-4 h-4 inline"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.11 0-2-.9-2-2V10c0-1.11.89-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .89 2 2z" />
                      </svg>
                    </span>
                  </li>
                  <li>Scroll down and tap "Add to Home Screen"</li>
                  <li>Tap "Add" in the top right corner</li>
                </ol>
              </div>
              <button
                onClick={handleDismiss}
                className="text-blue-200 hover:text-white p-1"
                aria-label="Close instructions"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <button
              onClick={handleDismiss}
              className="mt-3 w-full bg-white text-blue-600 px-4 py-2 rounded font-semibold hover:bg-blue-50 transition-colors"
            >
              Got It
            </button>
          </>
        )}
      </div>
    </div>
  );
}
